import { useId, type ReactElement } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  Spinner,
} from '@doxyme/ui/components/foundations';
import { ThemeProvider } from '@doxyme/ui/theme';
import { useIntl } from '@doxyme/locale';
import { AppletFrame } from '@doxyme/extensions-bridge-react';
import { useThemeNameByRoute } from '../../../hooks/useThemeNameByRoute';
import { useExtensionsModalApplet } from '../../../hooks/useExtensions/useExtensionsModalApplet';
import Box from '@mui/material/Box';

/**
 * The minimum height of the modal body.
 *
 * This is currently set to match the content of the _Interpreter_ applet.
 * It doesn't look too bad, if the modal needs to resize to fit different-sized applets,
 * but we matched _Interpreter_ exactly, as it is the only extension with a modal applet, currently.
 *
 * Demo: https://www.loom.com/share/21bd253744564df4a9b849068c8c736e
 * w/ different sized applet content: https://www.loom.com/share/7d5e1f47c39d429da006059d04f662e1
 *
 * A possible more advanced solution, to have no height change from the loading state to the applet content,
 * for all applets, would be to provide the "loading" height via the extension's manifest.
 * */
const MIN_MODAL_BODY_HEIGHT = 264;

/** The offset of the loading spinner from the top of the modal body */
const SPINNER_OFFSET = 0.3;

/**
 * The ModalAppletCapability needs a modal that it can render into.
 * This component is responsible for rendering modal frame applet.
 */
export function ExtensionsModal(): ReactElement {
  const { formatMessage } = useIntl();

  const themeName = useThemeNameByRoute();

  // Extensions: This capability will yield an applet frame when an extension produces a modal applet
  const { applet, close, isOpen, isLoaded, maxHeight } =
    useExtensionsModalApplet();

  const titleId = useId();

  return (
    <ThemeProvider themeName={themeName}>
      <Modal
        isOpen={isOpen}
        aria-labelledby={titleId}
        styles={{
          root: { marginTop: 0, marginBottom: 0 },
          container: { maxHeight },
        }}
      >
        <ModalContent>
          <ModalHeader
            title={applet?.label ?? formatMessage({ id: 'appsModal.title' })}
            titleId={titleId}
            onClose={close}
          />

          <Box
            sx={{
              minHeight: MIN_MODAL_BODY_HEIGHT,
              width: '100%',
            }}
          >
            <Box
              style={{
                width: 'fit-content',
                margin: 'auto',
                paddingTop: MIN_MODAL_BODY_HEIGHT * SPINNER_OFFSET,
                display: isLoaded ? 'none' : 'block',
              }}
            >
              <Spinner disableShrink={false} mode='green' size={40} />
            </Box>

            <Box
              sx={{
                display: isLoaded ? 'block' : 'none',
              }}
            >
              <AppletFrame applet={applet?.frame} />
            </Box>
          </Box>
        </ModalContent>
      </Modal>
    </ThemeProvider>
  );
}
